/**
 * Document Validation case page — /staff-ops/documents/:sid
 *
 * Full parity with Employment + Landlord workspaces. Same operational
 * template: Claimed Financials → Per-Document Reviews → Cross-Document
 * Consistency → Fraud Indicators → Internal Report → PM Summary publish.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import VerificationWorkspaceShell from "@/components/VerificationWorkspaceShell";
import VerifiedReport from "@/components/VerifiedReport";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, FileText, Save, ClipboardCheck, Send, Undo2, ShieldAlert } from "lucide-react";
import ManualRiskSelector from "@/components/ManualRiskSelector";
import { DashboardSkeleton } from "@/components/Skeleton";
import { fmtDateTime } from "@/lib/dateFormat";

// ── Vocabularies ────────────────────────────────────────────────────────
const PAY_FREQ = [
  { v: "weekly", l: "Weekly" }, { v: "bi_weekly", l: "Bi-weekly" },
  { v: "semi_monthly", l: "Semi-monthly" }, { v: "monthly", l: "Monthly" },
  { v: "annual", l: "Annual" },
];
// Canonical five-state model (Phase D / 2026-06).
const DOC_STATUSES = [
  { v: "valid",              l: "Valid" },
  { v: "invalid",            l: "Invalid" },
  { v: "unable_to_validate", l: "Unable to Validate" },
  { v: "in_progress",        l: "In Progress" },
  { v: "not_attempted",      l: "Not Attempted" },
];

// Legacy vocabulary read from historical records is coerced to the
// canonical five-state model on load so the UI renders the new labels
// without requiring the backfill to finish first.
const LEGACY_DOC_STATUS_MAP = {
  authentic: "valid",
  verified: "valid",
  likely_fraudulent: "invalid",
  suspicious: "unable_to_validate",
  incomplete: "unable_to_validate",
  minor_variance: "unable_to_validate",
  not_provided: "not_attempted",
};
const normalizeDocStatus = (v) => LEGACY_DOC_STATUS_MAP[v] || v;
const CONSISTENCY = [
  { v: "consistent", l: "Consistent" },
  { v: "minor_variance", l: "Minor Variance" },
  { v: "inconsistent", l: "Inconsistent" },
  { v: "not_available", l: "Not Available" },
];
const FRAUD_INDICATOR_OPTIONS = [
  { v: "document_altered", l: "Document appears altered" },
  { v: "missing_pages", l: "Missing pages" },
  { v: "image_manipulation", l: "Image manipulation" },
  { v: "font_inconsistencies", l: "Font inconsistencies" },
  { v: "metadata_concerns", l: "Metadata concerns" },
  { v: "identity_mismatch", l: "Identity mismatch" },
  { v: "address_mismatch", l: "Address mismatch" },
  { v: "income_inconsistency", l: "Income inconsistency" },
  { v: "employer_inconsistency", l: "Employer inconsistency" },
  { v: "bank_deposit_inconsistency", l: "Bank deposit inconsistency" },
  { v: "unusual_formatting", l: "Unusual formatting" },
  { v: "ai_generated", l: "Possible AI-generated document" },
  { v: "unable_to_authenticate", l: "Unable to authenticate" },
  { v: "other", l: "Other" },
];
const OUTCOMES = [
  { v: "verified", l: "Verified — all documents authentic" },
  { v: "partially_verified", l: "Partially Verified" },
  { v: "unable_to_verify", l: "Unable to Verify" },
  { v: "discrepancy_identified", l: "Discrepancy Identified" },
];

const DOCUMENT_TYPES = [
  // v1.0.8 Batch C — the Rental Application itself is reviewable within the
  // EXISTING Document Validation report. Reviewed / Not Reviewed only — the
  // application is never forced into Valid/Invalid, review is optional and
  // does not gate the six-stage workflow. Staff controls all narrative.
  { k: "rental_application", l: "Rental Application",
    statusOptions: [
      { v: "reviewed", l: "Reviewed" },
      { v: "not_reviewed", l: "Not Reviewed" },
    ],
    checks: ["employment_info_consistent", "income_representation_consistent",
             "rental_history_consistent", "addresses_consistent",
             "identity_info_consistent", "application_complete"] },
  { k: "government_id", l: "Government ID",
    checks: ["identity_matches_application", "photo_authentic", "expiration_valid",
             "no_visible_alteration", "image_quality_acceptable", "no_missing_information"] },
  { k: "pay_stub", l: "Pay Stubs",
    checks: ["employer_matches_application", "employee_name_matches", "income_matches",
             "pay_frequency_consistent", "ytd_calculations_consistent",
             "no_formatting_concerns", "no_visible_edits", "no_missing_information"] },
  { k: "bank_statement", l: "Bank Statements",
    checks: ["account_holder_matches", "income_deposits_support_claim", "deposit_consistency",
             "no_nsf_activity", "no_missing_pages", "no_formatting_concerns",
             "no_visible_alterations"] },
  { k: "tax_document", l: "Tax Documents",
    checks: ["applicant_matches", "income_matches", "document_complete",
             "no_obvious_inconsistencies"] },
  { k: "lease_rental", l: "Lease / Rental Documents",
    checks: ["applicant_listed", "property_matches_application", "rent_matches_application",
             "dates_consistent", "signatures_present"] },
];

// ── Claimed Financials (§3) ─────────────────────────────────────────────
function ClaimedFinancialsCard({ sid, claimed, canEdit, onSaved }) {
  const [form, setForm] = useState(claimed || {});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(claimed || {}); setDirty(false); }, [claimed]);
  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setDirty(true); };
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        monthly_income: form.monthly_income ? Number(form.monthly_income) : null,
        years_employed: form.years_employed ? Number(form.years_employed) : null,
      };
      const { data } = await apiClient.put(`/staff-ops/case/${sid}/documents/claimed-financials`, payload);
      toast.success("Claimed financials saved");
      setDirty(false); onSaved?.(data.claimed_financials);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-4" data-testid="claimed-financials-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-slate-500" />
          <div className="ntn-eyebrow text-slate-600">Claimed Financial Information</div>
        </div>
        {claimed?.seeded_from_application && !claimed?.saved_at && (
          <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
            Auto-populated from Rental Application — review & save
          </span>
        )}
        {claimed?.saved_at && (
          <span className="text-[11px] text-slate-500">
            Saved {fmtDateTime(claimed.saved_at)}{claimed.saved_by_name ? ` · ${claimed.saved_by_name}` : ""}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Claimed Monthly Income</Label>
          <Input type="number" step="0.01" value={form.monthly_income ?? ""} onChange={(e) => set("monthly_income", e.target.value)} disabled={!canEdit} data-testid="cf-monthly-income" /></div>
        <div><Label className="text-xs">Employer</Label>
          <Input value={form.employer || ""} onChange={(e) => set("employer", e.target.value)} disabled={!canEdit} data-testid="cf-employer" /></div>
        <div><Label className="text-xs">Pay Frequency</Label>
          <Select value={form.pay_frequency || ""} onValueChange={(v) => set("pay_frequency", v)} disabled={!canEdit}>
            <SelectTrigger data-testid="cf-pay-frequency"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{PAY_FREQ.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Position</Label>
          <Input value={form.position || ""} onChange={(e) => set("position", e.target.value)} disabled={!canEdit} data-testid="cf-position" /></div>
        <div><Label className="text-xs">Years Employed</Label>
          <Input type="number" step="0.1" value={form.years_employed ?? ""} onChange={(e) => set("years_employed", e.target.value)} disabled={!canEdit} data-testid="cf-years" /></div>
        <div><Label className="text-xs">Bank Institution</Label>
          <Input value={form.bank_institution || ""} onChange={(e) => set("bank_institution", e.target.value)} disabled={!canEdit} data-testid="cf-bank" /></div>
        <div className="col-span-2"><Label className="text-xs">Other Income Sources</Label>
          <Textarea rows={2} value={form.other_income_sources || ""} onChange={(e) => set("other_income_sources", e.target.value)} disabled={!canEdit} data-testid="cf-other-income" /></div>
      </div>
      <div className="flex justify-end pt-1">
        <Button onClick={save} disabled={!canEdit || busy || !dirty} className="bg-[#0A192F] text-white" data-testid="cf-save">
          <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save Claimed Financials"}
        </Button>
      </div>
    </Card>
  );
}

// ── Per-Document Reviews (§5) ────────────────────────────────────────────
function DocumentReviewsCard({ findings, set, canEdit }) {
  const reviews = findings.document_reviews || {};
  const updDoc = (docKey, key, value) => {
    const next = { ...(reviews[docKey] || {}), [key]: value };
    set("document_reviews", { ...reviews, [docKey]: next });
  };
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-4" data-testid="document-reviews-card">
      <div className="ntn-eyebrow text-slate-600">Per-Document Review</div>
      <div className="text-xs text-slate-500 -mt-1">Review each uploaded document independently.</div>
      {DOCUMENT_TYPES.map((doc) => {
        const r = reviews[doc.k] || {};
        return (
          <div key={doc.k} className="border border-slate-200 rounded-md p-3 space-y-2" data-testid={`docreview-${doc.k}`}>
            <div className="flex items-center justify-between">
              <div className="font-medium text-sm text-slate-900">{doc.l}</div>
              <Select value={normalizeDocStatus(r.overall_status) || ""} onValueChange={(v) => updDoc(doc.k, "overall_status", v)} disabled={!canEdit}>
                <SelectTrigger className="w-48 h-8" data-testid={`docreview-${doc.k}-status`}><SelectValue placeholder="Overall Status" /></SelectTrigger>
                <SelectContent>{(doc.statusOptions || DOC_STATUSES).map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2 pl-1">
              {doc.checks.map((check) => (
                <div key={check} className="flex items-center gap-1.5">
                  <Checkbox id={`${doc.k}-${check}`} checked={!!r[check]}
                            onCheckedChange={(v) => updDoc(doc.k, check, !!v)} disabled={!canEdit}
                            data-testid={`docreview-${doc.k}-${check}`} />
                  <Label htmlFor={`${doc.k}-${check}`} className="text-xs text-slate-700 capitalize">
                    {check.replace(/_/g, " ")}
                  </Label>
                </div>
              ))}
            </div>
            <Textarea rows={1} placeholder="Notes" value={r.notes || ""}
                      onChange={(e) => updDoc(doc.k, "notes", e.target.value)}
                      disabled={!canEdit} data-testid={`docreview-${doc.k}-notes`} />
          </div>
        );
      })}
    </Card>
  );
}

// ── Cross-Document Consistency (§6) ─────────────────────────────────────
function CrossDocumentCard({ findings, set, canEdit }) {
  const cross = findings.cross_document || {};
  const upd = (k, v) => set("cross_document", { ...cross, [k]: v });
  const ROWS = [
    { k: "employer", l: "Employer consistent" },
    { k: "income", l: "Income consistent" },
    { k: "name", l: "Names consistent" },
    { k: "address", l: "Addresses consistent" },
    { k: "employment_dates", l: "Employment dates consistent" },
    { k: "rental_amount", l: "Rental amounts consistent" },
    { k: "property_address", l: "Property address consistent" },
    { k: "identity", l: "Government ID supports identity" },
  ];
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-3" data-testid="cross-doc-card">
      <div className="ntn-eyebrow text-slate-600">Cross-Document Consistency Review</div>
      {ROWS.map((row) => (
        <div key={row.k} className="grid grid-cols-3 gap-3 items-center">
          <Label className="text-sm text-slate-700">{row.l}</Label>
          <Select value={cross[row.k] || ""} onValueChange={(v) => upd(row.k, v)} disabled={!canEdit}>
            <SelectTrigger className="col-span-2" data-testid={`xdoc-${row.k}`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{CONSISTENCY.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ))}
      <div><Label className="text-xs">Reviewer notes</Label>
        <Textarea rows={2} value={cross.notes || ""} onChange={(e) => upd("notes", e.target.value)} disabled={!canEdit} data-testid="xdoc-notes" /></div>
    </Card>
  );
}

// ── Fraud Indicators (§7) ────────────────────────────────────────────────
function FraudIndicatorsCard({ findings, set, canEdit }) {
  const indicators = findings.fraud_indicators || [];
  const has = (code) => indicators.some((i) => i.code === code);
  const setNotes = (code, notes) => set("fraud_indicators",
    indicators.map((i) => i.code === code ? { ...i, notes } : i));
  const toggle = (code) => {
    if (has(code)) set("fraud_indicators", indicators.filter((i) => i.code !== code));
    else set("fraud_indicators", [...indicators, { code, notes: "" }]);
  };
  const findNotes = (code) => indicators.find((i) => i.code === code)?.notes || "";
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-3" data-testid="fraud-indicators-card">
      <div className="ntn-eyebrow text-slate-600">Fraud Indicators</div>
      <div className="text-xs text-slate-500 -mt-1">Each selected indicator requires notes. These feed Risk Intelligence automatically.</div>
      <div className="space-y-2">
        {FRAUD_INDICATOR_OPTIONS.map((opt) => (
          <div key={opt.v} className="border border-slate-200 rounded-md p-2 space-y-1.5">
            <div className="flex items-center gap-2">
              <Checkbox id={`fi-${opt.v}`} checked={has(opt.v)} onCheckedChange={() => toggle(opt.v)}
                        disabled={!canEdit} data-testid={`fi-${opt.v}`} />
              <Label htmlFor={`fi-${opt.v}`} className="text-sm text-slate-700">{opt.l}</Label>
            </div>
            {has(opt.v) && (
              <Textarea rows={1} placeholder="Notes (required)"
                        value={findNotes(opt.v)} onChange={(e) => setNotes(opt.v, e.target.value)}
                        disabled={!canEdit} data-testid={`fi-${opt.v}-notes`} />
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Internal Validation Report preview (§8) ─────────────────────────────
function InternalReportCard({ findings }) {
  const reviews = findings.document_reviews || {};
  const cross = findings.cross_document || {};
  const fraud = findings.fraud_indicators || [];
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-3" data-testid="documents-internal-report-card">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-4 w-4 text-slate-500" />
        <div className="ntn-eyebrow text-slate-600">Internal Validation Report (Preview)</div>
      </div>
      <div className="text-xs text-slate-500 -mt-1">Retained by NTN. Not shown to the Property Manager.</div>
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-2 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 px-3 py-2">
          <div>Document</div><div>Overall Status</div>
        </div>
        {DOCUMENT_TYPES.map((doc) => (
          <div key={doc.k} className="grid grid-cols-2 px-3 py-2 text-sm border-t border-slate-100">
            <div className="text-slate-700 font-medium">{doc.l}</div>
            <div className="text-slate-900">{(normalizeDocStatus(reviews[doc.k]?.overall_status) || "—").replace(/_/g, " ")}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm text-slate-700">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Cross-document</div>
          <div className="text-xs text-slate-600">{Object.keys(cross).filter((k) => k !== "notes").length} checks recorded</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Fraud indicators</div>
          <div className="text-xs text-slate-600">{fraud.length ? `${fraud.length} identified` : "None identified"}</div>
        </div>
      </div>
    </Card>
  );
}

// ── PM Summary card + Publish (mirrors Landlord) ────────────────────────
function PMSummaryCard({ sid, verification, canEdit, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [riskOpen, setRiskOpen] = useState(false);
  const isCompleted = verification?.status === "completed" && !!verification?.outcome;
  const published = verification?.pm_summary;
  const publishedAt = published?.published_at;
  const manualBanner = verification?.manual_status_banner || null;
  const loadPreview = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/staff-ops/case/${sid}/documents/summary-preview`);
      setPreview(data.preview);
    } catch (e) { /* silent */ }
  }, [sid]);
  useEffect(() => { if (isCompleted) loadPreview(); }, [isCompleted, loadPreview, verification]);

  const publish = async () => {
    setBusy(true);
    try { await apiClient.post(`/staff-ops/case/${sid}/documents/publish-summary`); toast.success("PM summary published"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Publish failed"); }
    finally { setBusy(false); }
  };
  const unpublish = async () => {
    setBusy(true);
    try { await apiClient.post(`/staff-ops/case/${sid}/documents/unpublish-summary`); toast.success("PM summary rescinded"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Unpublish failed"); }
    finally { setBusy(false); }
  };
  const source = published || preview;
  const reportPayload = source ? {
    ...source,
    bullets: (source.bullets || []).map((b) => ({ ...b, value: b.verified || b.claimed || "—" })),
    risk_indicators: source.risk_indicators || [],
  } : null;

  return (
    <div className="space-y-3" data-testid="pm-summary-card">
      <Card className="p-4 bg-white border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-slate-500" />
          <div>
            <div className="ntn-eyebrow text-slate-600">Property Manager Report</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {publishedAt
                ? <>Published <span data-testid="pm-summary-published-badge">{fmtDateTime(publishedAt)}</span> · {published.published_by_name}</>
                : (isCompleted ? "Preview only — not visible to the Property Manager until published."
                               : "Complete validation to enable publish.")}
            </div>
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {manualBanner ? (
            <span
              className={`text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 rounded-sm ring-1 ${
                manualBanner.tone === "green"  ? "bg-emerald-50 text-emerald-800 ring-emerald-200" :
                manualBanner.tone === "amber"  ? "bg-amber-50 text-amber-800 ring-amber-200" :
                manualBanner.tone === "orange" ? "bg-orange-50 text-orange-800 ring-orange-200" :
                                                  "bg-rose-50 text-rose-800 ring-rose-200"
              }`}
              data-testid="manual-risk-chip"
            >{manualBanner.label}</span>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 rounded-sm ring-1 bg-slate-50 text-slate-700 ring-slate-200"
                  data-testid="manual-risk-chip">Risk not set</span>
          )}
          <Button variant="outline" onClick={() => setRiskOpen(true)} disabled={!canEdit || !isCompleted}
                  className="rounded-md" data-testid="manual-risk-btn">
            <ShieldAlert className="h-4 w-4 mr-2" /> {manualBanner ? "Edit risk" : "Set risk"}
          </Button>
          {publishedAt ? (
            <Button variant="outline" onClick={unpublish} disabled={!canEdit || busy} className="rounded-md" data-testid="pm-unpublish-btn">
              <Undo2 className="h-4 w-4 mr-2" /> Rescind
            </Button>
          ) : null}
          <Button onClick={publish} disabled={!canEdit || busy || !isCompleted || !manualBanner}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-md" data-testid="pm-publish-btn"
                  title={!manualBanner ? "Set a risk banner before publishing" : ""}>
            <Send className="h-4 w-4 mr-2" /> {publishedAt ? "Re-publish" : "Publish to PM"}
          </Button>
        </div>
      </Card>
      <ManualRiskSelector open={riskOpen} onOpenChange={setRiskOpen}
                          track="documents" screeningId={sid} current={manualBanner}
                          onSaved={() => onChanged?.()} />
      {reportPayload ? (
        <VerifiedReport moduleName="Document Validation"
                        title="Verified Document Validation Report" Icon={ShieldCheck}
                        payload={reportPayload} testid="pm-summary-preview-report"
                        sectionLabel="Documents Reviewed" />
      ) : (
        <Card className="p-6 bg-white border-slate-200 text-xs text-slate-400 italic">
          Report will appear here once the validation is completed.
        </Card>
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
export default function DocumentValidationPage() {
  const { sid } = useParams();
  const [workspace, setWorkspace] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [findings, setFindings] = useState({});
  const [completeOpen, setCompleteOpen] = useState(false);
  const [outcome, setOutcome] = useState("verified");
  const [summary, setSummary] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, s] = await Promise.all([
        apiClient.get(`/staff-ops/case/${sid}/documents`),
        apiClient.get(`/staff-ops/staff`),
      ]);
      setWorkspace(w.data); setStaff(s.data);
      setFindings(w.data.verification?.findings || {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  }, [sid]);
  useEffect(() => { load(); }, [load]);
  if (loading || !workspace) return <AppShell><DashboardSkeleton /></AppShell>;

  const canEdit = workspace.can_edit || workspace.supervisor_override_available;
  const claimed = workspace.verification?.claimed_financials || {};
  const set = (k, v) => setFindings((prev) => ({ ...prev, [k]: v }));

  const saveDraft = async () => {
    try { await apiClient.post(`/staff-ops/case/${sid}/documents/save-draft`, { findings }); toast.success("Findings draft saved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };
  const submitComplete = async () => {
    try {
      await apiClient.post(`/staff-ops/case/${sid}/documents/complete`, { outcome, findings, summary });
      toast.success(`Validation completed · ${outcome.replace(/_/g, " ")}`);
      setCompleteOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Complete failed"); }
  };

  return (
    <AppShell>
      <VerificationWorkspaceShell track="documents" sid={sid} workspace={workspace} staff={staff}
                                   reload={load} onSaveDraft={saveDraft}
                                   onComplete={() => setCompleteOpen(true)}>
        <ClaimedFinancialsCard sid={sid} claimed={claimed} canEdit={canEdit} onSaved={load} />
        <DocumentReviewsCard findings={findings} set={set} canEdit={canEdit} />
        <CrossDocumentCard findings={findings} set={set} canEdit={canEdit} />
        <FraudIndicatorsCard findings={findings} set={set} canEdit={canEdit} />
        <InternalReportCard findings={findings} />
        <PMSummaryCard sid={sid} verification={workspace.verification} canEdit={canEdit} onChanged={load} />
      </VerificationWorkspaceShell>

      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="max-w-lg" data-testid="complete-verification-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Complete validation</DialogTitle>
            <DialogDescription>Pick the overall outcome. Risk Intelligence is updated automatically from your structured findings.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Outcome</Label>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger data-testid="complete-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>{OUTCOMES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Summary (optional)</Label>
              <Textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="complete-summary" /></div>
            <div className="text-xs text-slate-500 flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-md p-2">
              <FileText className="h-3.5 w-3.5 mt-0.5" />
              Risk Intelligence indicators are auto-generated from your structured findings on completion. The Property Manager Summary is published separately via the &quot;Publish to PM&quot; action.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)}>Cancel</Button>
            <Button onClick={submitComplete} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="complete-submit">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
